import { readSse } from './sse';
import type { Citation } from '@24talk/shared/citations';

export class ReplyFailure extends Error {
  constructor(
    readonly code: string,
    readonly diagnosticId?: string,
  ) {
    super('Reply unavailable');
  }
}

function pause(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const cancel = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', cancel);
      resolve();
    }, ms);
    signal.addEventListener('abort', cancel, { once: true });
  });
}

function createRequestSignal(parent: AbortSignal, timeoutMs: number) {
  const controller = new AbortController();
  const abortFromParent = () => controller.abort();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  if (parent.aborted) controller.abort();
  else parent.addEventListener('abort', abortFromParent, { once: true });

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeout);
      parent.removeEventListener('abort', abortFromParent);
    },
  };
}

export async function recoverReply(
  input: {
    url: string;
    body: string;
    signal: AbortSignal;
    onSession: (id: string) => void;
    onRecovering: () => void;
  },
  options = { deadlineMs: 160_000, delayMs: 1_000, requestTimeoutMs: 15_000 },
) {
  const deadline = Date.now() + options.deadlineMs;
  let failures = 0;
  while (Date.now() < deadline) {
    let pending = false;
    let complete = false;
    let text = '';
    let citations: Citation[] = [];
    let requestSignal: ReturnType<typeof createRequestSignal> | undefined;
    try {
      requestSignal = createRequestSignal(
        input.signal,
        Math.max(1, Math.min(options.requestTimeoutMs, deadline - Date.now())),
      );
      const response = await fetch(input.url, {
        method: 'POST',
        credentials: 'omit',
        body: input.body,
        headers: { 'Content-Type': 'application/json' },
        signal: requestSignal.signal,
      });
      if (!response.ok && response.status < 500)
        throw new ReplyFailure(response.status === 429 ? 'CHAT_RATE_LIMIT' : 'CHAT_ACCESS');
      await readSse(response, ({ event, data }) => {
        const value = data as {
          conversationId?: string;
          text?: string;
          citations?: Citation[];
          code?: string;
          diagnosticId?: string;
        } | null;
        if (event === 'session' && value?.conversationId) input.onSession(value.conversationId);
        if (event === 'delta') text += value?.text ?? '';
        if (event === 'citations') citations = value?.citations ?? [];
        if (event === 'done') complete = true;
        if (event === 'pending') pending = true;
        if (event === 'error')
          throw new ReplyFailure(value?.code ?? 'CHAT_FAILED', value?.diagnosticId);
      });
      if (complete && text.trim()) return { text, citations };
      if (!pending) throw new Error('Incomplete delivery');
      failures = 0;
    } catch (error) {
      if (input.signal.aborted) throw error;
      if (error instanceof ReplyFailure) throw error;
      if (++failures >= 3) throw new ReplyFailure('DELIVERY_UNCONFIRMED');
      input.onRecovering();
    } finally {
      requestSignal?.cleanup();
    }
    await pause(options.delayMs * (failures ? 2 ** (failures - 1) : 1), input.signal);
  }
  throw new ReplyFailure('DELIVERY_UNCONFIRMED');
}
